import { io } from "socket.io-client"
import useSigninUserWithCredentials from "./signin-with-credentials"
import config from "aldoo-ra/Config"
const { API_URL } = config


const showMockProviderDialog = ({ provider, mockData, onCompleted }) => {
    const overlay = document.createElement("div")
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;"

    const box = document.createElement("div")
    box.style.cssText = "background:#fff;border-radius:12px;padding:24px;min-width:320px;font-family:sans-serif;box-shadow:0 8px 32px rgba(0,0,0,0.2);"

    const title = document.createElement("p")
    title.style.cssText = "margin:0 0 4px;font-size:13px;font-weight:600;color:#555;text-transform:uppercase;letter-spacing:.05em;"
    title.textContent = `Mock ${provider.id} sign-in`

    const pre = document.createElement("pre")
    pre.style.cssText = "background:#f4f4f4;border-radius:8px;padding:12px;font-size:12px;margin:12px 0 16px;overflow:auto;color:#333;"
    pre.textContent = JSON.stringify(mockData, null, 2)

    const btn = document.createElement("button")
    btn.textContent = "OK — sign in with these credentials"
    btn.style.cssText = "width:100%;padding:10px;background:#4285F4;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;"
    btn.onclick = () => {
        document.body.removeChild(overlay)
        onCompleted({ ...mockData })
    }

    box.appendChild(title)
    box.appendChild(pre)
    box.appendChild(btn)
    overlay.appendChild(box)
    document.body.appendChild(overlay)
}

const startProviderAuth = ({ provider, onCompleted }) => {
    //If this is an email provider, sign in with email
    if (provider.id === "email") return onCompleted({ email: provider.data })

    // Check for mock credentials in config (DEV only)
    const mockData = config.MOCK_SIGN_IN_PROVIDERS?.[provider.id.toUpperCase()]
    if (mockData) return showMockProviderDialog({ provider, mockData, onCompleted })

    const openPopup = () => {
        const width = 600
        const height = 700
        const left = window.innerWidth / 2 - width / 2
        const top = window.innerHeight / 2 - height / 2

        return window.open(
            "",
            "",
            `toolbar=no, location=no, directories=no, status=no, menubar=no,
      scrollbars=no, resizable=no, copyhistory=no, width=${width},
      height=${height}, top=${top}, left=${left}`
        )
    }

    const socket = io(API_URL)
    let popup = openPopup()
    let finished = false
    let popupNavigated = false
    const storageKey = `oauth-result-${provider.id}`

    // Timeout: cancel if no auth event within 5 minutes
    const timeout = setTimeout(() => finish(false), 5 * 60 * 1000)

    const finish = (user) => {
        if (finished) return
        finished = true
        clearTimeout(timeout)
        clearInterval(storagePoll)
        window.removeEventListener('message', handleMessage)
        try { localStorage.removeItem(storageKey) } catch {}
        popup?.close()
        socket.disconnect()
        onCompleted(user)
    }

    // Primary: postMessage from popup (survives server restarts)
    const handleMessage = (event) => {
        if (event.origin !== API_URL) return
        if (event.data?.type !== `${provider.id}-auth-result`) return
        console.log(`[OAuth] ${provider.id} postMessage received`, event.data.user ? { name: event.data.user.name, email: event.data.user.email } : 'NO USER DATA')
        finish(event.data.user || false)
    }
    window.addEventListener('message', handleMessage)

    // Secondary: localStorage polling (fallback when window.opener is null due to COOP)
    try { localStorage.removeItem(storageKey) } catch {}
    const storagePoll = setInterval(() => {
        try {
            const item = JSON.parse(localStorage.getItem(storageKey))
            if (item && Date.now() - item.ts < 60000) {
                console.log(`[OAuth] ${provider.id} localStorage result received`, item.user ? { name: item.user.name, email: item.user.email } : 'NO USER DATA')
                finish(item.user || false)
            }
        } catch {}
    }, 500)

    // Tertiary: socket.io event (backward compat)
    socket.on(`${provider.id}`, (user) => {
        console.log(`[OAuth] ${provider.id} socket event received`, user ? { name: user.name, email: user.email, hasGoogleId: !!user.google_id } : 'NO USER DATA')
        finish(user)
    })

    socket.on("connect", () => {
        if (popupNavigated) return  // Don't re-navigate on reconnect
        const authUrl = `${API_URL}/${provider.id}?socketId=${socket.id}`
        if (!popup) {
            // Popup was blocked (common on iOS) — redirect the current page instead
            clearTimeout(timeout)
            clearInterval(storagePoll)
            window.removeEventListener('message', handleMessage)
            socket.disconnect()
            window.location.href = authUrl
            return
        }
        popup.location.href = authUrl
        popupNavigated = true
    })
}

const useSigninWithProvider = () => {
    const { state, error, signinUser } = useSigninUserWithCredentials()
    const signin = async function (provider) {
        //3rd party provider, start the auth process
        startProviderAuth({
            provider,
            onCompleted: (user) => {
                //TODO: handle the error here if the user is not signed in
                if (!user) return

                // Add provider-specific fields like require to the user credentials
                if (provider.require) {
                    user.require = provider.require
                }

                // Map provider picture fields to avatar
                if (!user.avatar) user.avatar = user.picture || user.image

                //sign in the user with the provider
                //The user object is return by the 3rd party provider
                //and contains the user's email, name, avatar, etc
                signinUser(user)
            },
        })
    }
    return { state, error, signin }
}

export default useSigninWithProvider